import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { FiChevronRight, FiPlus, FiX } from "react-icons/fi";
import { emitFabMenuOpened, type FabMenuId, onFabMenuOpened } from "@/components/fabMenuEvents.ts";
import { useTranslation } from "react-i18next";
import { useLocation } from "@tanstack/react-router";

export interface FloatingActionItem {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

interface FloatingActionMenuProps {
  menuId: FabMenuId;
  items: FloatingActionItem[];
  ariaLabel: string;
  containerClassName?: string;
  containerStyle?: CSSProperties;
}

const ITEM_COLOR: Record<string, string> = {
  transaction: "var(--ds-card-sun)",
  saving: "var(--ds-card-lilac)",
};

export const FloatingActionMenu = ({
  menuId,
  items,
  ariaLabel,
  containerClassName,
  containerStyle,
}: FloatingActionMenuProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuPanelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const availableItems = useMemo(() => items.filter((item) => !item.disabled), [items]);
  const hasMenu = availableItems.length > 1;
  const triggerRight = containerStyle?.right ?? "var(--app-offset-x)";
  const triggerBottom = containerStyle?.bottom ?? "calc(env(safe-area-inset-bottom) + var(--app-space-4))";
  const menuIdValue = "create-menu-" + menuId;

  useEffect(() => {
    if (!hasMenu || !isOpen) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insidePanel = menuPanelRef.current?.contains(target);
      if (!insideTrigger && !insidePanel) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      const focused = document.activeElement;
      if (!menuPanelRef.current?.contains(focused) && !triggerRef.current?.contains(focused)) return;
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
        return;
      }
      const menuItems = menuItemRefs.current.filter((item): item is HTMLButtonElement => Boolean(item));
      if (!menuItems.length) return;
      if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        menuItems[event.key === "Home" ? 0 : menuItems.length - 1]?.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const current = menuItems.indexOf(document.activeElement as HTMLButtonElement);
      const next = current < 0
        ? (event.key === "ArrowDown" ? 0 : menuItems.length - 1)
        : (current + (event.key === "ArrowDown" ? 1 : -1) + menuItems.length) % menuItems.length;
      event.preventDefault();
      menuItems[next]?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasMenu, isOpen]);

  useEffect(
    () =>
      onFabMenuOpened((openedMenuId) => {
        if (openedMenuId !== menuId) setIsOpen(false);
      }),
    [menuId],
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  if (availableItems.length === 0) return null;

  const handlePrimaryClick = () => {
    if (!hasMenu) {
      availableItems[0]?.onSelect();
      return;
    }
    setIsOpen((current) => {
      const next = !current;
      if (next) {
        emitFabMenuOpened(menuId);
        requestAnimationFrame(() => menuItemRefs.current[0]?.focus());
      } else {
        triggerRef.current?.focus();
      }
      return next;
    });
  };

  const handleItemSelect = (item: FloatingActionItem) => {
    setIsOpen(false);
    triggerRef.current?.focus();
    item.onSelect();
  };

  const renderItems = () => availableItems.map((item, index) => {
    const color = ITEM_COLOR[item.id];
    return (
      <button
        key={item.id}
        ref={(element) => { menuItemRefs.current[index] = element; }}
        type="button"
        role="menuitem"
        onClick={() => handleItemSelect(item)}
        className="app-focus-ring flex min-h-[72px] w-full items-center gap-4 rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] px-4 py-3 text-left transition-colors hover:bg-[var(--ds-ground)]"
      >
        <span
          className={[
            "inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--ds-radius-md)] bg-[var(--ds-surface)] text-[var(--ds-ink)]",
            color ? "text-[var(--ds-on-card)]" : "bg-[var(--ds-surface)]",
          ].join(" ")}
          style={color ? { backgroundColor: color } : undefined}
          aria-hidden="true"
        >
          {item.icon ?? <FiPlus className="h-5 w-5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-medium leading-[22px] text-[var(--ds-ink)]">{item.label}</span>
          {item.description ? <span className="block truncate text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">{item.description}</span> : null}
        </span>
        <FiChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-ink-muted)]" aria-hidden="true" />
      </button>
    );
  });

  return (
    <>
      {hasMenu && isOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-[color:color-mix(in_srgb,var(--ds-ground)_80%,transparent)]"
          onClick={() => {
            setIsOpen(false);
            triggerRef.current?.focus();
          }}
          aria-label={t("common:actions.close")}
        />
      ) : null}

      {hasMenu && isOpen ? (
        <div
          id={menuIdValue}
          ref={menuPanelRef}
          className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+var(--app-space-4)+var(--app-fab-size)+var(--app-space-3))] z-60 flex max-h-[calc(100dvh-10rem-env(safe-area-inset-bottom))] flex-col gap-2 overflow-y-auto overscroll-contain md:bottom-[calc(var(--app-menu-bottom)+var(--app-fab-size)+var(--app-space-3))] md:left-auto md:right-[var(--app-menu-right)] md:w-[min(400px,calc(100vw-2rem))] md:max-h-[calc(100dvh-2rem)]"
          style={{
            "--app-menu-right": triggerRight,
            "--app-menu-bottom": triggerBottom,
          } as CSSProperties}
          role="menu"
          aria-label={t("common:navigation.createMenu")}
        >
          {renderItems()}
        </div>
      ) : null}

      <div
        ref={containerRef}
        className={["fixed z-50 flex flex-col items-end", containerClassName ?? ""].join(" ")}
        style={{ right: triggerRight, bottom: triggerBottom, ...containerStyle }}
      >
        <button
          ref={triggerRef}
          type="button"
          onClick={handlePrimaryClick}
          className="app-focus-ring app-fab static inline-flex h-[var(--app-fab-size)] w-[var(--app-fab-size)] items-center justify-center rounded-full border-[3px] border-[var(--ds-ground)] bg-[var(--ds-card-flame)] text-[var(--ds-on-card)] shadow-[var(--ds-shadow-3)] transition-colors hover:brightness-95"
          aria-label={ariaLabel}
          aria-expanded={hasMenu ? isOpen : undefined}
          aria-haspopup={hasMenu ? "menu" : undefined}
          aria-controls={hasMenu && isOpen ? menuIdValue : undefined}
        >
          {isOpen ? <FiX className="h-[26px] w-[26px]" aria-hidden="true" /> : <FiPlus className="h-[26px] w-[26px]" aria-hidden="true" />}
        </button>
      </div>
    </>
  );
};
